import React from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { EventPopup } from '../services/api/popupApi';

// 다른 사이트로 가는 링크만 새 탭으로 연다(이 사이트 안의 /retreat/info/ 같은 주소는 그대로 이동).
const isExternal = (url: string) => /^https?:\/\//i.test(url) && !url.startsWith(window.location.origin);

interface Props {
  popup: EventPopup;
  open: boolean;
  onClose: () => void;
  onHideToday: () => void;
}

// 팝업 한 장 — 첫 화면(EventPopup)과 관리자 미리 보기(PopupManagePage)가 함께 쓴다.
const EventPopupDialog: React.FC<Props> = ({ popup, open, onClose, onHideToday }) => {
  const link = popup.linkUrl;
  const linkProps = link
    ? { href: link, ...(isExternal(link) ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }
    : null;
  const image = (
    <Box component="img" src={popup.imageUrl} alt={popup.title} sx={{ display: 'block', width: '100%', height: 'auto' }} />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="event-popup-title"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ position: 'relative' }}>
        {linkProps ? (
          <a {...linkProps} tabIndex={-1} aria-hidden="true">
            {image}
          </a>
        ) : image}
        <IconButton
          aria-label="팝업 닫기"
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,.85)', color: '#1c2042', '&:hover': { bgcolor: '#fff' } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, textAlign: 'center' }}>
        <Typography id="event-popup-title" variant="h6" sx={{ fontWeight: 800, wordBreak: 'keep-all' }}>
          {popup.title}
        </Typography>
        {popup.text && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
            {popup.text}
          </Typography>
        )}
        {linkProps && (
          <Button {...linkProps} variant="contained" fullWidth size="large" sx={{ mt: 2, fontWeight: 800 }}>
            {popup.buttonText || '자세히 보기'}
          </Button>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Button size="small" color="inherit" onClick={onHideToday}>오늘 하루 보지 않기</Button>
          <Button size="small" color="inherit" onClick={onClose}>닫기</Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EventPopupDialog;
